import { Static, Type } from '@sinclair/typebox';

export type VideoRefKind = 'api_video' | 'youtube' | 'invalid';

export interface ParsedVideoRef {
  kind: VideoRefKind;
  id: string;
  /** YouTube Shorts are inherently vertical (9:16) — lets consumers default to a portrait layout. */
  isShort?: boolean;
}

export interface VideoEmbedOptions {
  autoplay?: boolean;
  muted?: boolean;
  loop?: boolean;
  controls?: boolean;
}

export function buildVideoEmbedUrl(videoRef: string, options: VideoEmbedOptions = {}): string | null {
  const parsed = parseVideoRef(videoRef);
  if (parsed.kind === 'youtube') {
    const params = new URLSearchParams();
    if (options.autoplay) params.set('autoplay', '1');
    if (options.muted) params.set('mute', '1');
    if (options.loop) {
      params.set('loop', '1');
      params.set('playlist', parsed.id);
    }
    if (options.controls === false) params.set('controls', '0');
    params.set('rel', '0');
    params.set('modestbranding', '1');
    params.set('iv_load_policy', '3');
    params.set('playsinline', '1');
    return `https://www.youtube-nocookie.com/embed/${parsed.id}?${params.toString()}`;
  }
  if (parsed.kind === 'api_video') {
    const params = new URLSearchParams();
    if (options.autoplay) params.set('autoplay', 'true');
    if (options.muted) params.set('muted', 'true');
    if (options.loop) params.set('loop', 'true');
    if (options.controls === false) params.set('hide-controls', 'true');
    params.set('hide-title', 'true');
    if (options.loop) params.set('hide-poster', 'true');
    const qs = params.toString();
    return `https://embed.api.video/vod/${parsed.id}${qs ? `?${qs}` : ''}`;
  }
  return null;
}

export function parseVideoRef(ref: string | undefined | null): ParsedVideoRef {
  if (!ref || ref.trim().length === 0) return { kind: 'invalid', id: '' };
  const trimmed = ref.trim();

  const ytWatch = trimmed.match(/(?:youtube\.com\/watch\?.*v=)([a-zA-Z0-9_-]{11})/);
  if (ytWatch) return { kind: 'youtube', id: ytWatch[1] };

  const ytShareLink = trimmed.match(/(?:youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  if (ytShareLink) return { kind: 'youtube', id: ytShareLink[1] };

  // YouTube Shorts (vertical) — e.g. https://www.youtube.com/shorts/abcDEF12345
  const ytShorts = trimmed.match(/(?:youtube(?:-nocookie)?\.com\/shorts\/)([a-zA-Z0-9_-]{11})/);
  if (ytShorts) return { kind: 'youtube', id: ytShorts[1], isShort: true };

  const ytEmbed = trimmed.match(/(?:youtube(?:-nocookie)?\.com\/embed\/)([a-zA-Z0-9_-]{11})/);
  if (ytEmbed) return { kind: 'youtube', id: ytEmbed[1] };

  if (/^https?:\/\//i.test(trimmed)) return { kind: 'invalid', id: '' };

  return { kind: 'api_video', id: trimmed };
}

export const VOD_MAX_TITLE_LENGTH = 200;
export const VOD_MAX_UPLOAD_SIZE_BYTES = 500 * 1024 * 1024;

/** Max number of videos attachable to a single prestation (api.video hosting/delivery cost guard). */
export const PRESTATION_MAX_VIDEOS = 5;

/**
 * A single video attached to an entity (e.g. a prestation). `video_ref` is either an
 * api.video id or a YouTube URL — resolved client-side via `parseVideoRef` / `buildVideoEmbedUrl`.
 * Stored verbatim (no Thumbor signing), unlike picture S3 keys.
 */
export const PrestationVideoSchema = Type.Object({
  video_ref: Type.String(),
  title: Type.Optional(Type.String()),
});
export type PrestationVideoObject = Static<typeof PrestationVideoSchema>;

export const VodUploadVideoResponseSchema = Type.Object({
  api_video_id: Type.String(),
  status: Type.String(),
  iframe_url: Type.Optional(Type.String()),
  hls_url: Type.Optional(Type.String()),
  thumbnail_url: Type.Optional(Type.String()),
  mp4_url: Type.Optional(Type.String()),
});
export type VodUploadVideoResponseObject = Static<typeof VodUploadVideoResponseSchema>;

export const VodDeleteResponseSchema = Type.Object({
  api_video_id: Type.String(),
  deleted: Type.Boolean(),
});
export type VodDeleteResponseObject = Static<typeof VodDeleteResponseSchema>;
